<template>
  <div class="cms-form-block">
    <div class="container">
      <div
        v-if="titleH2"
        class="cms-form-block__title h2"
        v-html="titleH2"
      />
      <div
        v-if="message"
        class="cms-form-block__message h5"
        v-html="message"
      />
      <div class="cms-form-block__form">
        <template v-for="(formElement) in formSettings">
          <template v-if="formElement.name === 'city_name'">
            <div
              class="cms-form-element"
              :class="[formElement.size].join(' ')"
            >
              <CustomSelectCities
                v-model="formValues.city_name"
                class-select="cms-form-element__input"
              />
            </div>
          </template>
          <template v-else>
            <div
              class="cms-form-element"
              :class="[formElement.size].join(' ')"
            >
              <input
                v-model="formValues[formElement.name]"
                v-maska
                :data-maska="formElement.dataMask"
                data-maska-tokens="F:[0,3,4,9]"
                type="text"
                class="cms-form-element__input"
                :name="formElement.name"
                :placeholder="formElement.placeholder"
              />

              <div
                v-for="(error) in ($v?.value?.[formElement.name]?.$errors || [])"
                class="cms-form-element__error"
              >
                {{ error.$message }}
              </div>
            </div>
          </template>
        </template>
        <template v-if="isVisibleSelectedTheme">
          <div class="cms-form-element --1-3">
            <div
              class="cms-form-element__select"
              :class="{'--selected': Boolean(formValues.theme)}"
            >
              <CustomSelect
                :active-value="selectedTheme"
                :select-list="messageSubject"
                :form-type="props?.values?.type"
                @emitOptions="changeFormValues"
              />
            </div>
            <div
              v-for="(error) in ($v?.value?.theme?.$errors || [])"
              class="cms-form-element__error"
            >
              {{ error.$message }}
            </div>
          </div>
        </template>
        <template v-if="messageVisible">
          <div
            class="cms-form-element --2-3"
            :class="{'--1-1': !isVisibleSelectedTheme}"
          >
            <textarea
              v-model="formValues.message"
              type="text"
              class="cms-form-element__input --textarea"
              :placeholder="messagePlaceholder"
            />
            <div
              v-for="(error) in ($v?.value?.message?.$errors || [])"
              class="cms-form-element__error"
            >
              {{ error.$message }}
            </div>
          </div>
        </template>

        <template v-if="isVisibleSelectedTheme || is3dProject">
          <div class="cms-form-element --1-1">
            <client-only>
              <FormImages
                :use-low-preview="true"
                :images="formValues.file"
                @onChange="changeFormImages"
              />
            </client-only>
          </div>
        </template>
      </div>
      <div class="cms-form-block__action">
        <div class="--button">
          <button
            class="btn btn-primary"
            :class="{'--loading': isSendig}"
            :disabled="Boolean(isSendig || !isAccept)"
            @click="submitForm"
          >
            {{ isSendig ? 'Секундочку пожалуйста ...' : buttonText }}
          </button>
        </div>
        <div class="--accept">
          <label>
            <input
              v-model="isAccept"
              type="checkbox"
              class="checked"
            />
            <span class="h5">Согласие на обработку персональных данных</span>
          </label>
        </div>
      </div>
    </div>

    <div class="cms-form-block__bg">
      <svg
        width="159"
        height="38"
        viewBox="0 0 159 38"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M41.7251 20.5215C45.5435 23.5039 49.3179 26.4531 53.0913 29.0859C60.5061 34.2578 69.6604 37.3145 79.5662 37.3145C89.4719 37.3145 98.6262 34.2578 106.041 29.0859C109.814 26.4531 113.589 23.5039 117.407 20.5215C130.236 10.498 143.561 0.0878906 159 0H0C15.4956 0.0292969 28.8594 10.4688 41.7251 20.5215Z"
          fill="white"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue';
import { email, helpers, minLength, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import CustomSelect from '~/components/UI/CustomSelect.vue';
import formBlockMessageSubject from '~/constants/form-block-message-subject.json';
import { useCommonStore } from '~/store/common';
import { useNotificationModal } from '~/composables/useNotificationModal';
import FormImages from '~/components/widget/FormImages.vue';
import CustomSelectCities from "~/components/common/CustomSelectCities.vue";

interface IItemFormSetting {
	name: string;
	size: string;
	title?: string;
	dataMask?: string;
	required?: boolean;
	placeholder?: string;
}

const notificationModal = useNotificationModal();
const props = defineProps<IBlockDefaultProps>();
const { $api, $toast, $ym } = useNuxtApp();

const titleH2: ComputedRef<string> = computed(() => {
  return props?.values?.heading || '';
});
const message: ComputedRef<string> = computed(() => {
  return 'Если у вас возникнут вопросы, вы всегда можете задать их нам';
});
const buttonText: ComputedRef<string> = computed(() => {
  return props.values?.buttonText || 'Отправить';
});
const messageVisible: ComputedRef<boolean> = computed(() => {
  return Boolean(props.values?.message || 0);
});
const messagePlaceholder: ComputedRef<string> = computed(() => {
  return props.values?.message_placeholder || 'Комментарий';
});
const messageSubject: ComputedRef<any[]> = computed(() => {
  return (formBlockMessageSubject.options || []).map((t: string) => {
    return {
      name: t
    };
  });
});

const isVisibleSelectedTheme: ComputedRef<boolean> = computed(() => {
  return Boolean(props.values?.type === 'Свяжитесь с нами')
});

const passedReachGoal: ComputedRef<string> = computed(() => {
  return props.values.goal || '';
})

const isAccept: Ref<boolean> = ref(true);

const is3dProject: ComputedRef<boolean> = computed(() => {
  console.log('asdaskjdkhsakjhdjksha')
  const isLabel = Boolean(props.values.heading === 'Заказать 3Д проект');
  const isPage = Boolean(useRoute().path === '/contact-us');
  console.log('useRoute().path: ', useRoute().path)
  console.log('useRoute().path: ', useRoute())

  return isLabel || isPage
});
const formSettings: Ref<IItemFormSetting[]> = ref([]);
const initFormSettings = () => {
  const _formSettings: IItemFormSetting[] = [];

  let itemsMainElement = [props.values?.name, props.values?.phone, props.values?.email].filter((t) => !!t).length;
  if (is3dProject.value) {
    itemsMainElement = itemsMainElement + 1;
  }

  const mainSize = `--1-${itemsMainElement}`;

  if (props.values?.name) {
    _formSettings.push({
      name: 'name',
      size: mainSize,
      title: props.values?.name_name || '',
      placeholder: props.values?.name_placeholder || '',
      required: Boolean(props.values?.name_required || 0)
    });
  }
  if (props.values?.phone) {
    _formSettings.push({
      name: 'phone',
      size: mainSize,
      title: props.values?.phone_name || '',
      placeholder: '+7(___) ___-__-__',
      required: Boolean(props.values?.phone_required || 0),
      dataMask: '+7(F##) ###-##-##'
    });
  }
  if (props.values?.email) {
    _formSettings.push({
      name: 'email',
      size: mainSize,
      title: props.values?.email_name || '',
      placeholder: props.values?.email_placeholder || '',
      required: Boolean(props.values?.email_required || 0)
    });
  }
  if (is3dProject.value) {
    _formSettings.push({
      name: 'city_name',
      size: mainSize,
    });
  }

  formSettings.value = _formSettings;
};
Promise.all([
  initFormSettings()
]);

const formValues: Ref<any> = ref({
  name: '',
  theme: '',
  email: '',
  phone: '',
  message: '',
  city_name: '',
  file: [],
});
const $v: Ref<any> = ref(null);
const selectedTheme: ComputedRef<string> = computed(() => {
  return formValues.value?.theme || 'Выбрать тему сообщения *';
});
const changeFormValues = (event1: any) => {
  formValues.value.theme = event1?.name || '';
};
const initValidation = () => {
  const _formRules: any = {};

  if (isVisibleSelectedTheme.value) {
    _formRules.theme = {
      required: helpers.withMessage('Обязательно к заполнению', required)
    };
  }

  if (props.values?.name) {
    _formRules.name = {
      required: helpers.withMessage('Обязательно к заполнению', required)
    };
  }
  if (props.values?.phone && props.values?.phone_required) {
    _formRules.phone = {
      required: helpers.withMessage('Обязательно к заполнению', required),
      minLength: helpers.withMessage('Неверный формат', minLength(17))
    };
  }
  if (props.values?.email && props.values?.email_required) {
    _formRules.email = {
      email: helpers.withMessage('Неверный формат', email),
      required: helpers.withMessage('Обязательно к заполнению', required)
    };
  }
  if (props.values?.message && props.values?.message_required) {
    _formRules.message = {
      required: helpers.withMessage('Обязательно к заполнению', required)
    };
  }

  $v.value = useVuelidate(_formRules, formValues);
};
const changeFormImages = (images: any[]) => {
  formValues.value.file = images;
};
onMounted(() => {
  initValidation();
});

const isSendig: Ref<boolean> = ref(false);
const submitForm = async () => {
  const isFormCorrect = await $v.value.value.$validate();
  if (!isFormCorrect) {
    return false;
  }

  isSendig.value = true;

  let message: string[] = [formValues.value.message];
  if (formValues.value.theme) {
    message.push(`Тема обращения: ${formValues.value.theme}`);
  }
  if (formValues?.value?.city_name) {
    message.push(`Город, выбранный клиентом в заявке: ${formValues?.value?.city_name}`);
  }

  const bodySend: any = {
    type: props.values?.type || props.values?.heading || 'Форма обратной связи',
    name: formValues.value.name,
    phone: (formValues.value.phone || '').replace(/\D+/g, ''),
    message: message.join('. </br>'),
    email: formValues.value.email,
    url: window.location.href,
    title: props.values?.heading || '',
    no_mail_send: Number(!formValues.value.email),
    additional_data: {
      city_name: formValues.value.city_name
    }
  };
  if (!formValues.value.email) {
    delete bodySend.email;
  }
  if (!formValues.value.phone) {
    delete bodySend.phone;
  }
  if (!bodySend.additional_data.city_name) {
    delete bodySend.additional_data.city_name;
  }

  const formData = new FormData();
  Object.keys(bodySend).map((key) => {
    if (['additional_data'].includes(key)) {
      formData.append(key, JSON.stringify(bodySend[key] || {}));
    } else {
      formData.append(key, bodySend[key]);
    }
  });
  (formValues.value.file || []).map((file: any) => {
    formData.append('attachments[]', file);
  });

  const res = await $api.agent.post('/form/send', {
    body: formData
  }).then((res) => {
    return res._data;
  }).catch((err) => {
    return { error: (err.response?._data || []) };
  });
  if (res.error) {
    isSendig.value = false;
    const _errMessage = res.error.map((t: any) => t.message).join(';</br>');
    $toast.error(_errMessage || 'Ошибка сервера');
    return;
  }

  formValues.value = {
    name: '',
    theme: '',
    email: '',
    phone: '',
    message: ''
  };
  $v.value.value.$reset();

  isSendig.value = false;
  if (typeof $ym === 'function' && passedReachGoal.value) {
    $ym('reachGoal', passedReachGoal.value);
  }

  notificationModal.openNotificationModal({
    title: "Заявка принята",
    message: "Совсем скоро мы проверим вашу заявку и свяжемся с вами в течение пары часов"
  });
};
</script>

<style lang="scss">
.cms-form-block {
	position: relative;
	z-index: 0;
	background-color: #F4F3F1;
	border-radius: 40px 40px 0 0;
	padding-top: 64px;
	padding-bottom: 129px;
	margin-bottom: -120px;
}

.cms-form-block__message {
	margin-top: 8px;
}

.cms-form-block__form {
	display: flex;
	flex-wrap: wrap;
	margin-top: -20px;
	margin-left: -20px;
	padding-top: 32px;
	box-sizing: border-box;

	& > * {
		margin-top: 20px;
		margin-left: 20px;
	}

	.--1-3 {
		width: calc(100% / 3 - 20px);
	}

	.--1-4 {
		width: calc(100% / 4 - 20px);
	}

	.--1-2 {
		width: calc(100% / 2 - 20px);
	}

	.--2-3 {
		width: calc((100% / 3) * 2 - 20px);
	}

  .--1-1 {
    width: calc(100% - 20px);
  }

  .form-images {
    margin: 0!important;
  }
  .form-images .--label {
    display: none;
  }
}

.cms-form-block__action {
	display: flex;
	align-items: center;
	margin-top: 12px;

	.--button {
		width: 100vw;
		max-width: 413px;
		margin-right: 20px;
	}

	.--button .btn {
		width: 100%;
	}

	.--accept label {
		display: flex;
		align-items: center;
		cursor: pointer;

		span {
			line-height: 100%;
			margin-left: 16px;
		}

		input {
			border-color: #B8B8B8;
		}

		input:checked {
			border-color: black;
		}
	}

	.--accept span {
		flex: 1;
	}
}

.cms-form-block__bg {
	position: absolute;
	z-index: -1;
	top: 0;
	left: 61%;
}

.cms-form-element {
}

.cms-form-element__input {
	width: 100%;
	padding: 17px 23px;
	box-sizing: border-box;
	border-radius: 16px;
  min-height: 60px;
	border: 1px solid #B8B8B8;
	transition: all 0.2s linear;

	font-size: 12px;
	font-weight: 600;

	&.--textarea {
		display: block;
		height: 60px;
	}

	&:focus {
		background-color: white;
	}

	&::placeholder {
		color: #989898;
	}
}

.cms-form-element__select {
	.select.open .select-element {
		border-color: #B8B8B8;
		background-color: white;
	}

	.select-element {
		height: initial;
		padding: 17px 23px;
		box-sizing: border-box;
		border-radius: 16px;
		border: 1px solid #B8B8B8;

		font-size: 12px;
		font-weight: 600;
		color: #989898;
		line-height: 24px;
	}

	.select-dropdown {
		max-height: 190px;
		overflow: auto;
	}

	.select-element img {
		top: 50%;
		transform: translateY(-50%);
	}

	&.--selected .select-element {
		color: #000000;
	}
}

.cms-form-element__error {
	font-size: 12px;
	font-weight: 500;
	color: #ED908C;
}

@media (max-width: 1023px) {
	.cms-form-block {
		padding-top: 40px;
		padding-bottom: 112px;
		border-radius: 24px 24px 0 0;
		margin-bottom: -100px;
	}
	.cms-form-block__title {
		text-align: center;
	}
	.cms-form-block__message {
		margin-top: 16px;
		text-align: center;
	}
	.cms-form-block__form {
		flex-direction: column;
		margin-top: -16px;
		margin-left: 0;
		padding-top: 40px;

		& > * {
			margin-top: 16px;
			margin-left: 0;
			width: 100% !important;
		}
	}
	.cms-form-block__action {
		flex-direction: column-reverse;
		margin-top: 16px;

		.--button {
			width: 100%;
			margin-top: 16px;
			margin-right: 0;
		}

		.--accept .h5 {
			font-size: 12px;
		}
	}
	.cms-form-block__bg {
		display: none;
	}

	.cms-form-element__input {
		padding: 11px 23px;
		border-radius: 12px;
    min-height: 48px;
	}
  .cms-form-element__select {
    .select-element {
      padding: 11px 23px;
      border-radius: 12px;
      min-height: 48px;
    }
  }
}
</style>
